import React, { useState, useEffect } from 'react';
import { Card, Button, Spin, message, Row, Col } from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import apiClient from '../../../utils/apiClient';
import { downloadCSV } from '../../../utils/csvExport';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface DayOfWeekData {
  day_of_week: string;
  day_number: number;
  usage_count: number;
}

interface TimeSlotData {
  time_slot: string;
  usage_count: number;
}

const TimeAnalytics: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [dayOfWeekData, setDayOfWeekData] = useState<DayOfWeekData[]>([]);
  const [timeSlotData, setTimeSlotData] = useState<TimeSlotData[]>([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/coupon-analytics/analytics/by-time');

      if (response.data.success) {
        setDayOfWeekData(response.data.data.dayOfWeek);
        setTimeSlotData(response.data.data.timeSlot);
      } else {
        message.error('データ取得に失敗しました');
      }
    } catch (error) {
      console.error('曜日・時間帯別分析エラー:', error);
      message.error('データ取得エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleExportCSV = () => {
    const csvData = [
      { セクション: '曜日別', 項目: '', 使用数: '' },
      ...dayOfWeekData.map(row => ({
        セクション: '',
        項目: row.day_of_week,
        使用数: row.usage_count
      })),
      { セクション: '', 項目: '', 使用数: '' },
      { セクション: '時間帯別', 項目: '', 使用数: '' },
      ...timeSlotData.map(row => ({
        セクション: '',
        項目: row.time_slot,
        使用数: row.usage_count
      }))
    ];

    downloadCSV(csvData, '曜日・時間帯別分析.csv');
    message.success('CSVファイルをダウンロードしました');
  };

  // 曜日別グラフデータ
  const dayOfWeekChartData = {
    labels: dayOfWeekData.map(d => d.day_of_week),
    datasets: [
      {
        label: '使用数',
        data: dayOfWeekData.map(d => d.usage_count),
        backgroundColor: [
          'rgba(255, 99, 132, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
          'rgba(255, 159, 64, 0.6)',
          'rgba(199, 199, 199, 0.6)'
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)',
          'rgba(199, 199, 199, 1)'
        ],
        borderWidth: 1
      }
    ]
  };

  // 時間帯別グラフデータ
  const timeSlotChartData = {
    labels: timeSlotData.map(d => d.time_slot),
    datasets: [
      {
        label: '使用数',
        data: timeSlotData.map(d => d.usage_count),
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  const totalUsage = dayOfWeekData.reduce((sum, d) => sum + d.usage_count, 0);
  const maxDay = dayOfWeekData.reduce((max, d) => d.usage_count > max.usage_count ? d : max, dayOfWeekData[0]);
  const maxTimeSlot = timeSlotData.reduce((max, d) => d.usage_count > max.usage_count ? d : max, timeSlotData[0]);

  return (
    <div>
      {/* アクションボタン */}
      <Card className="mb-4">
        <div className="flex justify-end gap-2">
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchData}
            loading={loading}
          >
            更新
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportCSV}
            disabled={dayOfWeekData.length === 0}
          >
            CSV出力
          </Button>
        </div>
      </Card>

      {loading ? (
        <Card>
          <div className="flex justify-center items-center" style={{ minHeight: 400 }}>
            <Spin size="large" />
          </div>
        </Card>
      ) : (
        <>
          {/* サマリーカード */}
          <Row gutter={16} className="mb-4">
            <Col span={8}>
              <Card>
                <div className="text-center">
                  <div className="text-gray-500 mb-2">総使用数</div>
                  <div className="text-3xl font-bold text-blue-600">{totalUsage}枚</div>
                </div>
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <div className="text-center">
                  <div className="text-gray-500 mb-2">最も多い曜日</div>
                  <div className="text-3xl font-bold text-green-600">
                    {maxDay?.day_of_week || '-'}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {maxDay?.usage_count || 0}枚
                  </div>
                </div>
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <div className="text-center">
                  <div className="text-gray-500 mb-2">最も多い時間帯</div>
                  <div className="text-lg font-bold text-orange-600">
                    {maxTimeSlot?.time_slot || '-'}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {maxTimeSlot?.usage_count || 0}枚
                  </div>
                </div>
              </Card>
            </Col>
          </Row>

          {/* 曜日別グラフ */}
          <Card title="曜日別使用状況" className="mb-4">
            {dayOfWeekData.length > 0 ? (
              <Bar data={dayOfWeekChartData} options={chartOptions} />
            ) : (
              <div className="text-center text-gray-500 py-12">
                データがありません
              </div>
            )}
          </Card>

          {/* 時間帯別グラフ */}
          <Card title="時間帯別使用状況">
            {timeSlotData.length > 0 ? (
              <Bar data={timeSlotChartData} options={chartOptions} />
            ) : (
              <div className="text-center text-gray-500 py-12">
                データがありません
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default TimeAnalytics;
